import { Badge, Checkbox, Group, Select, Stack, Table, Text } from '@mantine/core';
import {
  BINARY_CAPABILITIES,
  CAPABILITY_GROUPS,
  CAPABILITY_LABELS,
  isProtectedCapability,
  SCOPES,
  scopeRank,
  type Capability,
  type Grant,
  type Scope,
} from '@prooffleet/contracts';

const SCOPE_LABEL: Record<Scope, string> = {
  none: 'None',
  own: 'Own',
  site: 'Site',
  org: 'Organization',
};

/** Действие в столбце матрицы. Ключ права строится как `ресурс:действие`. */
const ACTIONS = [
  { key: 'read', label: 'View' },
  { key: 'create', label: 'Create' },
  { key: 'write', label: 'Edit' },
  { key: 'assign', label: 'Assign' },
  { key: 'close', label: 'Close' },
  { key: 'invite', label: 'Invite' },
] as const;

interface GrantMatrixProps {
  grants: Grant[];
  /** Права редактора: шире собственных выдать нельзя (правило 15). */
  actorGrants: readonly Grant[];
  onChange: (grants: Grant[]) => void;
  disabled?: boolean;
}

function isScope(value: string | null): value is Scope {
  return value !== null && (SCOPES as readonly string[]).includes(value);
}

function scopeOf(grants: readonly Grant[], capability: Capability): Scope {
  return grants.find((g) => g.capability === capability)?.scope ?? 'none';
}

function setScope(grants: Grant[], capability: Capability, scope: Scope): Grant[] {
  const rest = grants.filter((g) => g.capability !== capability);
  return scope === 'none' ? rest : [...rest, { capability, scope }];
}

/**
 * Матрица прав.
 *
 * Шесть строк вместо сорока чекбоксов: роль читается целиком за пять секунд.
 * Область выбирается в ячейке, потому что право отвечает на вопрос ЧТО,
 * а область — НАСКОЛЬКО ШИРОКО; без этого разделения роли размножаются
 * комбинаторно.
 */
export function GrantMatrix({
  grants,
  actorGrants,
  onChange,
  disabled = false,
}: GrantMatrixProps): React.JSX.Element {
  /** Область, шире которой редактор выдать не может. */
  const ceiling = (capability: Capability): Scope => scopeOf(actorGrants, capability);

  const availableScopes = (capability: Capability): Scope[] =>
    SCOPES.filter((s) => scopeRank(s) <= scopeRank(ceiling(capability)));

  return (
    <Stack gap="lg">
      <Table.ScrollContainer minWidth={720}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Resource</Table.Th>
              {ACTIONS.map((action) => (
                <Table.Th key={action.key}>{action.label}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {CAPABILITY_GROUPS.map((group) => (
              <Table.Tr key={group.resource}>
                <Table.Td>
                  <Text fw={600}>{group.label}</Text>
                </Table.Td>
                {ACTIONS.map((action) => {
                  const capability = `${group.resource}:${action.key}` as Capability;
                  const exists = (group.capabilities as readonly string[]).includes(capability);
                  if (!exists) {
                    return (
                      <Table.Td key={action.key}>
                        <Text c="dimmed">—</Text>
                      </Table.Td>
                    );
                  }

                  const max = ceiling(capability);
                  return (
                    <Table.Td key={action.key}>
                      <Select
                        size="xs"
                        allowDeselect={false}
                        // Недоступное право показываем отключённым, а не прячем:
                        // иначе непонятно, почему его нельзя выдать.
                        disabled={disabled || max === 'none'}
                        data={availableScopes(capability).map((s) => ({
                          value: s,
                          label: SCOPE_LABEL[s],
                        }))}
                        value={scopeOf(grants, capability)}
                        onChange={(value) => {
                          if (isScope(value)) onChange(setScope(grants, capability, value));
                        }}
                      />
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <div>
        <Text fw={600} mb="xs">
          Special permissions
        </Text>
        <Stack gap="xs">
          {BINARY_CAPABILITIES.map((capability) => {
            const protectedCap = isProtectedCapability(capability);
            const unavailable = ceiling(capability) === 'none';
            return (
              <Group key={capability} gap="sm" wrap="nowrap">
                <Checkbox
                  disabled={disabled || protectedCap || unavailable}
                  checked={scopeOf(grants, capability) !== 'none'}
                  label={CAPABILITY_LABELS[capability]}
                  onChange={(event) =>
                    onChange(
                      setScope(grants, capability, event.currentTarget.checked ? 'org' : 'none'),
                    )
                  }
                />
                {protectedCap && (
                  <Badge size="xs" color="red" variant="outline">
                    system role only
                  </Badge>
                )}
                {!protectedCap && unavailable && (
                  <Badge size="xs" color="gray" variant="outline">
                    you don't have it
                  </Badge>
                )}
              </Group>
            );
          })}
        </Stack>
      </div>
    </Stack>
  );
}
